import React, { useMemo, useState } from 'react';
import ChartCanvas from './ChartCanvas';
import Gauge from './Gauge';
import {
  fmtNum, fmtDate, fmtDuration, titleCase, platformLabel, initial,
  topPlatforms,
} from './soundstatsHelpers';
import {
  lineConfig, horizBarConfig, doughnutConfig, releasesBarConfig,
} from './soundstatsCharts';

// Overview (core group) body. Every card/section is hidden when its data is
// unavailable or error-flagged.

const SC_COLOR = '#00dc49';
const FB_COLOR = '#00dc49';
const TR_COLOR = '#00dc49';

function Delta({ cur, prev }) {
  if (cur == null || prev == null) return null;
  const d = cur - prev;
  if (d > 0) return <div className="ss-g-delta up">▲ {fmtNum(d)}</div>;
  if (d < 0) return <div className="ss-g-delta down">▼ {fmtNum(Math.abs(d))}</div>;
  return <div className="ss-g-delta flat">— 0</div>;
}

function Kpi({ value, label }) {
  return (
    <div className="ss-kpi">
      <div className="ss-kpi-v">{fmtNum(value)}</div>
      <div className="ss-kpi-l">{label}</div>
    </div>
  );
}

function useExpandable(collapsible, fullLimit) {
  const [expanded, setExpanded] = useState(false);
  const limit = collapsible ? (expanded ? Infinity : 10) : fullLimit;
  return { limit, showExpand: collapsible && !expanded, expand: () => setExpanded(true) };
}

function ExpandBtn({ onClick, total, label }) {
  return (
    <button type="button" className="ss-expand" onClick={onClick}>
      Show all {total} {label}
    </button>
  );
}

export default function Overview({ N, AVAIL, collapsible = false }) {
  const meta = N.meta || {};
  const latest = N.score.length ? N.score[N.score.length - 1] : {};
  const first = N.score.length ? N.score[0] : {};

  const scoreCfg = useMemo(() => {
    if (!N.score.length) return null;
    return lineConfig(
      N.score.map(it => new Date(it.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
      [
        { label: 'SC Score', data: N.score.map(it => it.scScore), color: SC_COLOR },
        { label: 'Fanbase', data: N.score.map(it => it.fanbaseScore), color: FB_COLOR },
        { label: 'Trending', data: N.score.map(it => it.trendingScore), color: TR_COLOR },
      ],
    );
  }, [N.score]);

  const socialData = topPlatforms(N.stats?.social);
  const streamingData = topPlatforms(N.stats?.streaming);
  const popData = topPlatforms(N.stats?.popularity);
  const retData = topPlatforms(N.stats?.retention);

  const followersCfg = useMemo(() => (socialData.length ? horizBarConfig(socialData.map(d => d.label), socialData.map(d => d.value)) : null), [socialData]);
  const listenersCfg = useMemo(() => (streamingData.length ? horizBarConfig(streamingData.map(d => d.label), streamingData.map(d => d.value)) : null), [streamingData]);
  const popCfg = useMemo(() => (popData.length ? horizBarConfig(popData.map(d => d.label), popData.map(d => d.value)) : null), [popData]);
  const retCfg = useMemo(() => (retData.length ? horizBarConfig(retData.map(d => d.label), retData.map(d => d.value)) : null), [retData]);

  const yearCounts = useMemo(() => {
    const y = {};
    N.albums.forEach(a => {
      if (!a.releaseDate) return;
      const k = String(a.releaseDate).slice(0, 4);
      y[k] = (y[k] || 0) + 1;
    });
    return y;
  }, [N.albums]);
  const yearKeys = Object.keys(yearCounts).sort();
  const releasesCfg = useMemo(() => (yearKeys.length ? releasesBarConfig(yearKeys, yearCounts) : null), [yearKeys, yearCounts]);

  const genreData = (meta.genres || []).map(x => x.root).filter(Boolean);
  const genreCfg = useMemo(() => (genreData.length ? doughnutConfig(genreData, genreData.map((_, i) => 1 + (genreData.length - i))) : null), [genreData]);

  const radioData = (N.broadcastGroups || []).slice(0, 10).map(it => ({
    label: (it.radio && it.radio.name) || '—',
    value: it.playCount || it.spinCount || it.count || 0,
  }));
  const radioCfg = useMemo(() => (radioData.length ? horizBarConfig(radioData.map(d => d.label), radioData.map(d => d.value)) : null), [radioData]);

  const albumsLimit = useExpandable(collapsible, 30);
  const songsLimit = useExpandable(collapsible, 30);
  const eventsLimit = useExpandable(collapsible, 24);
  const relatedLimit = useExpandable(collapsible, 24);

  const showStats = AVAIL.statsSocial || AVAIL.statsStreaming || AVAIL.statsPopularity || AVAIL.statsRetention;
  const showCatalog = AVAIL.albums || AVAIL.songs || AVAIL.meta || AVAIL.identifiers;
  const showRadio = AVAIL.broadcastGroups || AVAIL.broadcasts;
  const showLive = AVAIL.events || AVAIL.related;

  const facts = [
    ['Birth date', meta.birthDate ? fmtDate(meta.birthDate) : null],
    ['Gender', meta.gender ? titleCase(meta.gender) : null],
    ['Type', meta.type ? titleCase(meta.type) : null],
    ['Career stage', meta.careerStage ? titleCase(meta.careerStage) : null],
    ['Growth level', meta.growthLevel ? titleCase(meta.growthLevel) : null],
    ['City', meta.cityName], ['Country', meta.countryCode],
    ['ISNI', meta.isni], ['IPI', meta.ipi],
  ].filter(f => f[1]);

  return (
    <>
      <section className="ss-kpi-grid">
        {AVAIL.songs && <Kpi value={N.songs.length} label="Tracks" />}
        {AVAIL.albums && <Kpi value={N.albums.length} label="Releases" />}
        {AVAIL.events && <Kpi value={N.events.length} label="Events" />}
        {AVAIL.related && <Kpi value={N.related.length} label="Similar artists" />}
        {AVAIL.broadcasts && <Kpi value={N.broadcasts.length} label="Airplays" />}
        {AVAIL.broadcastGroups && <Kpi value={N.broadcastGroups.length} label="Radio stations" />}
      </section>

      {AVAIL.score && (
        <section className="ss-sec">
          <h2 className="ss-sec-title">Overview <span className="ss-sec-hint">Soundcharts score</span></h2>
          <div className="ss-grid ss-g-1-2">
            <div className="ss-card ss-card-gauge">
              <div className="ss-card-head">Soundcharts score <span className="ss-hint">current</span></div>
              <div className="ss-gauge-row">
                <div className="ss-gauge-cell"><Gauge value={latest.scScore} label="SC Score" color={FB_COLOR} max={100000} /><Delta cur={latest.scScore} prev={first.scScore} /></div>
                <div className="ss-gauge-cell"><Gauge value={latest.fanbaseScore} label="Fanbase" color={SC_COLOR} max={100000} /><Delta cur={latest.fanbaseScore} prev={first.fanbaseScore} /></div>
                <div className="ss-gauge-cell"><Gauge value={latest.trendingScore} label="Trending" color={TR_COLOR} max={100000} /><Delta cur={latest.trendingScore} prev={first.trendingScore} /></div>
              </div>
            </div>
            <div className="ss-card">
              <div className="ss-card-head">Score trend <span className="ss-hint">time series</span></div>
              {scoreCfg && <ChartCanvas config={scoreCfg} />}
            </div>
          </div>
        </section>
      )}

      {showStats && (
        <section className="ss-sec">
          <h2 className="ss-sec-title">Current Stats <span className="ss-sec-hint">social, streaming, popularity, retention</span></h2>
          <div className="ss-grid ss-g-2">
            {AVAIL.statsSocial && followersCfg && (
              <div className="ss-card"><div className="ss-card-head">Followers by platform <span className="ss-hint">social fans</span></div><ChartCanvas config={followersCfg} /></div>
            )}
            {AVAIL.statsStreaming && listenersCfg && (
              <div className="ss-card"><div className="ss-card-head">Streaming audience by platform <span className="ss-hint">listeners / plays</span></div><ChartCanvas config={listenersCfg} /></div>
            )}
            {AVAIL.statsPopularity && popCfg && (
              <div className="ss-card"><div className="ss-card-head">Popularity index <span className="ss-hint">by platform</span></div><ChartCanvas config={popCfg} /></div>
            )}
            {AVAIL.statsRetention && retCfg && (
              <div className="ss-card"><div className="ss-card-head">Fan retention <span className="ss-hint">conversion %</span></div><ChartCanvas config={retCfg} /></div>
            )}
          </div>
        </section>
      )}

      {showCatalog && (
        <section className="ss-sec">
          <h2 className="ss-sec-title">Catalog & Identity <span className="ss-sec-hint">releases, genres, profile</span></h2>
          <div className="ss-grid ss-g-3">
            {AVAIL.albums && releasesCfg && (
              <div className="ss-card"><div className="ss-card-head">Releases by year</div><ChartCanvas config={releasesCfg} /></div>
            )}
            {AVAIL.meta && genreCfg && (
              <div className="ss-card"><div className="ss-card-head">Genre distribution</div><ChartCanvas config={genreCfg} /></div>
            )}
            {AVAIL.meta && (
              <div className="ss-card">
                <div className="ss-card-head">Key facts</div>
                <div className="ss-kv">
                  {facts.length > 0 ? facts.map((f, i) => (
                    <div key={i} className="ss-kv-row"><span className="ss-kv-k">{f[0]}</span><span className="ss-kv-v">{f[1]}</span></div>
                  )) : <span className="ss-empty">No facts available.</span>}
                </div>
              </div>
            )}
          </div>
          <div className="ss-grid ss-g-2">
            {AVAIL.albums && (
              <div className="ss-card">
                <div className="ss-card-head">Releases <span className="ss-hint">{N.albums.length} shown</span></div>
                <div className="ss-table-wrap"><table className="ss-table"><thead><tr><th>Title</th><th>Type</th><th>Release date</th></tr></thead><tbody>
                  {N.albums.slice(0, albumsLimit.limit).map((a, i) => (
                    <tr key={i}><td className="ss-td-title">{a.name}</td><td><span className="ss-tag">{a.type || 'release'}</span></td><td className="ss-td-dim">{fmtDate(a.releaseDate)}</td></tr>
                  ))}
                  {N.albums.length === 0 && <tr><td colSpan={3} className="ss-td-dim">No releases</td></tr>}
                </tbody></table></div>
                {albumsLimit.showExpand && N.albums.length > 10 && <ExpandBtn onClick={albumsLimit.expand} total={N.albums.length} label="releases" />}
              </div>
            )}
            {AVAIL.songs && (
              <div className="ss-card">
                <div className="ss-card-head">Tracks <span className="ss-hint">{N.songs.length} shown</span></div>
                <div className="ss-table-wrap"><table className="ss-table"><thead><tr><th>Title</th><th>Credit</th><th>Release date</th></tr></thead><tbody>
                  {N.songs.slice(0, songsLimit.limit).map((s, i) => (
                    <tr key={i}><td className="ss-td-title">{s.name}</td><td className="ss-td-dim">{s.creditName || '—'}</td><td className="ss-td-dim">{fmtDate(s.releaseDate)}</td></tr>
                  ))}
                  {N.songs.length === 0 && <tr><td colSpan={3} className="ss-td-dim">No tracks</td></tr>}
                </tbody></table></div>
                {songsLimit.showExpand && N.songs.length > 10 && <ExpandBtn onClick={songsLimit.expand} total={N.songs.length} label="tracks" />}
              </div>
            )}
          </div>
          {AVAIL.identifiers && (
            <div className="ss-card">
              <div className="ss-card-head">Platforms & profiles</div>
              <div className="ss-link-list">
                {N.identifiers.slice(0, 24).map((it, i) => {
                  const label = it.platformName || platformLabel(it.platform) || 'Link';
                  const url = it.url || it.link || it.externalUrl || it.appUrl || '#';
                  return (
                    <a key={i} className="ss-link" href={url} target="_blank" rel="noopener noreferrer">
                      <span className="ss-link-ico">{initial(label)}</span>
                      <span>{label}</span>
                      {it.verified && <span className="ss-verified">✓</span>}
                    </a>
                  );
                })}
                {N.identifiers.length === 0 && <span className="ss-empty">No profiles available.</span>}
              </div>
            </div>
          )}
        </section>
      )}

      {showRadio && (
        <section className="ss-sec">
          <h2 className="ss-sec-title">Radio Airplay <span className="ss-sec-hint">spins across stations</span></h2>
          <div className="ss-grid ss-g-2">
            {AVAIL.broadcastGroups && radioCfg && (
              <div className="ss-card"><div className="ss-card-head">Top radio stations <span className="ss-hint">by plays</span></div><ChartCanvas config={radioCfg} /></div>
            )}
            {AVAIL.broadcasts && (
              <div className="ss-card">
                <div className="ss-card-head">Recent airplay</div>
                <div className="ss-stream-list">
                  {N.broadcasts.slice(0, 24).map((b, i) => {
                    const song = b.song || {};
                    const radio = b.radio || {};
                    const title = song.name || song.creditName || '—';
                    return (
                      <div key={i} className="ss-stream">
                        <div className="ss-stream-img">{initial(title)}</div>
                        <div className="ss-stream-body">
                          <div className="ss-stream-song">{title}</div>
                          <div className="ss-stream-meta">{[radio.name, radio.cityName].filter(Boolean).join(' · ')}</div>
                        </div>
                        <div className="ss-stream-time">{fmtDuration(b.duration)}</div>
                      </div>
                    );
                  })}
                  {N.broadcasts.length === 0 && <span className="ss-empty">No airplay data.</span>}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {showLive && (
        <section className="ss-sec">
          <h2 className="ss-sec-title">Live & Similar Artists <span className="ss-sec-hint">events and related acts</span></h2>
          <div className="ss-grid ss-g-2">
            {AVAIL.events && (
              <div className="ss-card">
                <div className="ss-card-head">Upcoming events <span className="ss-hint">{N.events.length} total</span></div>
                <div className="ss-ev-grid">
                  {N.events.slice(0, eventsLimit.limit).map((e, i) => {
                    const v = e.venue || {};
                    const loc = [v.cityName || e.cityName, v.countryName || e.countryName].filter(Boolean).join(', ');
                    return (
                      <div key={i} className="ss-ev-card">
                        <div className="ss-ev-date">{fmtDate(e.date || e.startedAt || e.dateTime)}</div>
                        <div className="ss-ev-name">{e.name || 'Event'}</div>
                        <div className="ss-ev-venue">{v.name || ''}</div>
                        {loc && <div className="ss-ev-loc">{loc}</div>}
                      </div>
                    );
                  })}
                  {N.events.length === 0 && <span className="ss-empty">No upcoming events.</span>}
                </div>
                {eventsLimit.showExpand && N.events.length > 10 && <ExpandBtn onClick={eventsLimit.expand} total={N.events.length} label="events" />}
              </div>
            )}
            {AVAIL.related && (
              <div className="ss-card">
                <div className="ss-card-head">Similar artists <span className="ss-hint">{N.related.length} total</span></div>
                <div className="ss-rel-grid">
                  {N.related.slice(0, relatedLimit.limit).map((r, i) => {
                    const sub = [r.careerStage, r.countryCode].filter(Boolean).map(titleCase).join(' · ');
                    return (
                      <div key={i} className="ss-rel-card">
                        {r.imageUrl ? (
                          <img className="ss-rel-img" src={r.imageUrl} alt={r.name} loading="lazy" />
                        ) : (
                          <div className="ss-rel-img ph">{initial(r.name)}</div>
                        )}
                        <div className="ss-rel-name">{r.name || '—'}</div>
                        {sub && <div className="ss-rel-sub">{sub}</div>}
                      </div>
                    );
                  })}
                  {N.related.length === 0 && <span className="ss-empty">No similar artists.</span>}
                </div>
                {relatedLimit.showExpand && N.related.length > 10 && <ExpandBtn onClick={relatedLimit.expand} total={N.related.length} label="similar artists" />}
              </div>
            )}
          </div>
        </section>
      )}

      <footer className="ss-foot">Data via Lyve AIA · Soundcharts artist intelligence</footer>
    </>
  );
}
